<script setup>
import { ref, nextTick, watch, computed, onMounted, onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'
import { useDisplay } from 'vuetify'
import { useRoute, useRouter } from 'vue-router'
import { useAgentChat } from '@/composables/useAgentChat'
import { linkedChatId } from '@/utils/chatLink'
import { useVoiceTranscription } from '@/composables/useVoiceTranscription'
import { useReservationsStore } from '@/stores/reservations.store'
import { useUsersStore } from '@/stores/users.store'
import AgentChatMessage from './AgentChatMessage.vue'
import AgentChatToolStatus from './AgentChatToolStatus.vue'
import AgentReservationSummary from './AgentReservationSummary.vue'
import AgentTraceModal from './AgentTraceModal.vue'
import SupportChatDialog from './SupportChatDialog.vue'
import AgentHelpDialog from './AgentHelpDialog.vue'
import UpgradeProfileDialog from './UpgradeProfileDialog.vue'

const props = defineProps({
  pageMode: { type: Boolean, default: false },

  // sheetMode renders the chat inside a partial-height bottom sheet (the
  // Platinum landing "RM AI launcher") rather than full-screen. It initializes
  // the conversation like pageMode but fills its container instead of the
  // viewport, shows starter chips, and emits close/expand for the host sheet.
  sheetMode: { type: Boolean, default: false },
  agentType: { type: String, default: 'member' },
})

const emit = defineEmits(['close', 'expand'])

const { smAndDown } = useDisplay()
const router = useRouter()
const route = useRoute()

const {
  connected,
  isOpen,
  hasBeenOpened,
  messages,
  streamingContent,
  isTyping,
  conversationId,
  conversations,
  conversationsLoading,
  currentToolCall,
  error,
  isProcessing,
  isAdmin,
  activeAgentType,
  accessStatus,
  accessLoading,
  connect,
  sendMessage,
  stopRequested,
  stopGeneration,
  regenerate,
  listConversations,
  loadConversation,
  startNewConversation,
  resumeOrStartConversation,
  openLinkedConversation,
  switchAgentType,
  toggleChat,
  closeChat,
  checkAccess,
  pendingReservationDialogId,
  pendingReservationDialogStep,
  pendingReservationRestaurantId,
  clearPendingReservationDialog,
  confirmedRestaurant,
  confirmedBookingMember,
  reservationSummary,
  reservationDraftId,
  groupThread,
} = useAgentChat()

// RM-398 — a group text thread is read here and continued on the phone. One
// tap reopens it there: the phone's own messaging link, addressed to RM's
// number and everyone else on the thread, which lands on the existing thread
// with those same people. iPhone and Mac take the addresses as a query;
// Android takes them after the scheme.
const groupTextHref = computed(() => {
  const numbers = groupThread.value?.openNumbers || []
  if (!numbers.length) return ''
  const list = numbers.join(',')
  const apple = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent || '')

  return apple ? `sms://open?addresses=${list}` : `sms:${list}`
})

const groupPeopleLine = computed(() => (groupThread.value?.people || []).join(', '))

const reservationsStore = useReservationsStore()
const usersStore = useUsersStore()
const { myUser, loginDialogMode } = storeToRefs(usersStore)

// The regenerate action lives on the newest AI reply only.
const lastAssistantIdx = computed(() => {
  for (let i = messages.value.length - 1; i >= 0; i--) {
    if (messages.value[i].role === 'assistant') return i
  }

  return -1
})
const userFirstName = computed(() => myUser.value?.firstName || '')

const isLoggedIn = computed(() => !!myUser.value && myUser.value.userLevel !== 'dummy')

// Upgrade dialog state (non-platinum logged-in users)
const showUpgradeDialog = ref(false)
const upgradeProductId = ref('individual-1cycle-annual')
const upgradePriceDollars = ref(300)

function handleRequestPlatinum() {
  upgradeProductId.value = 'individual-1cycle-annual'
  upgradePriceDollars.value = 300
  showUpgradeDialog.value = true
}

function handleRequestToJoin() {
  loginDialogMode.value = 'activate'
}

async function onUpgradeDialogComplete() {
  showUpgradeDialog.value = false
  await usersStore.getMyUser()
  await checkAccess()
}

function onUpgradeDialogCancel() {
  showUpgradeDialog.value = false
}

// Post-submit navigation: after reservation dialog closes, navigate to /bookings
watch(pendingReservationDialogId, async id => {
  if (id) {
    const step = pendingReservationDialogStep.value
    clearPendingReservationDialog()

    // Listen for dialog close to navigate to bookings
    const unwatch = watch(
      () => reservationsStore.editingReservation,
      val => {
        if (!val) {
          unwatch()
          router.push('/bookings')
        }
      },
    )
    await reservationsStore.openReservationDialogAtStep(id, step)
  }
})

const inputText = ref('')
const inputRef = ref(null)
const messagesContainer = ref(null)
const agentContentRef = ref(null)
const showHistory = ref(false)
const historySearch = ref('')

const filteredConversations = computed(() => {
  const q = historySearch.value.trim().toLowerCase()
  if (!q) return conversations.value

  return conversations.value.filter(c => c.title?.toLowerCase().includes(q))
})

// Trace modal state
const traceModalOpen = ref(false)
const traceMsgDbIndex = ref(null)

// RM-212: in-place support chat over the AI conversation.
const supportDialogOpen = ref(false)

// "Questions?" panel behind the ? in a reply's action row — email, support,
// and the FAQs in one place, at the moment help is most useful (before a
// request goes to the restaurant).
const helpDialogOpen = ref(false)


function handleSend() {
  const text = inputText.value.trim()
  const attachment = pendingAttachment.value
  if ((!text && !attachment?.imageId) || isProcessing.value) return
  if (attachment?.uploading) return
  if (props.sheetMode) emit('expand')

  // RM-163 analytics: whether any part of this message came from voice.
  sendMessage(text, {
    inputMethod: voiceUsedSinceLastSend.value ? 'voice_transcription' : 'typed',
    attachedImageId: attachment?.imageId || null,
    attachedFileType: attachment?.isPdf ? 'pdf' : 'image',
  })
  voiceUsedSinceLastSend.value = false
  inputText.value = ''
  removeAttachment()
  nextTick(() => autoGrowInput())
}

// ── Chat attachment (receipts, profile photos) ─────────────────────────────
// Tap the attach button, pick an image or a PDF → it uploads through the existing
// authenticated POST /media route and shows as a pending chip above the
// composer. Send carries the imageId; the AI can then set it as the member's
// profile photo via update_my_profile.
const attachInputRef = ref(null)
const pendingAttachment = ref(null) // { imageId, previewUrl, uploading, isPdf, name }

/**
 * Look BEFORE sending (Kapil, 2026-09-23: "allow me to magnify before I enter
 * it - same as I do on you"). A card is the one attachment where a member wants
 * to check the photo is sharp enough to read before RM tries to — a blurry card
 * is cheaper to retake than to correct after RM misreads it. The dialog shows
 * the member's own file at full resolution, not an upload rendition.
 */
const attachZoomOpen = ref(false)
const attachError = ref('')
const MAX_ATTACH_MB = 20 // must match MAX_UPLOAD_MB in lib/mediaUploadGuards.js

function handleAttachTap() {
  attachError.value = ''
  attachInputRef.value?.click()
}

async function handleAttachFile(e) {
  const file = e.target.files?.[0]

  e.target.value = '' // allow re-picking the same file
  if (!file) return
  // Receipts are often PDFs (Kapil, 2026-09-17); profile photos stay image-only server-side.
  const isPdf = file.type === 'application/pdf'
  if (!file.type?.startsWith('image/') && !isPdf) {
    attachError.value = 'Please choose an image or a PDF.'

    return
  }
  if (file.size > MAX_ATTACH_MB * 1024 * 1024) {
    attachError.value = `File too large — max ${MAX_ATTACH_MB}MB.`

    return
  }

  removeAttachment()

  const previewUrl = URL.createObjectURL(file)

  pendingAttachment.value = { imageId: null, previewUrl, uploading: true, isPdf, name: file.name }

  try {
    const formData = new FormData()

    formData.append('image', file)

    const res = await fetch('/media/', { method: 'POST', body: formData })
    if (!res.ok) {
      let serverError = null
      try {
        serverError = (await res.json())?.error
      } catch (err) { /* non-JSON error body */ }
      throw new Error(serverError || 'Upload failed. Please try again.')
    }

    const imageId = (await res.text()).trim()

    // Only apply if the member didn't remove/replace the attachment mid-upload.
    if (pendingAttachment.value?.previewUrl === previewUrl) {
      pendingAttachment.value = { imageId, previewUrl, uploading: false, isPdf, name: file.name }
    }
  } catch (err) {
    attachError.value = err.message || 'Upload failed. Please try again.'
    if (pendingAttachment.value?.previewUrl === previewUrl) removeAttachment()
  }
}

function removeAttachment() {
  if (pendingAttachment.value?.previewUrl) URL.revokeObjectURL(pendingAttachment.value.previewUrl)
  pendingAttachment.value = null
}

function handleKeydown(e) {
  if (e.key === 'Enter' && !e.shiftKey && !smAndDown.value) {
    e.preventDefault()
    handleSend()
  }
}

function autoGrowInput() {
  const el = inputRef.value
  if (!el) return
  el.style.height = '0px'
  const maxHeight = smAndDown.value ? 180 : 120
  const newHeight = Math.min(el.scrollHeight, maxHeight)
  el.style.height = newHeight + 'px'
  el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden'

  // Keep caret visible on iOS when text wraps
  el.scrollTop = el.scrollHeight
}

// ── Voice-to-text (RM-163) ──────────────────────────────────────────────────
// Tap the mic, speak, tap stop → the transcript appends to the composer for
// review; the member edits and sends like any typed message. Cancel discards
// the take. Transcription is server-side; the mic is hidden where
// MediaRecorder/getUserMedia don't exist.
const voiceUsedSinceLastSend = ref(false)

function appendTranscript(text) {
  const existing = inputText.value || ''
  const sep = existing && !/\s$/.test(existing) ? ' ' : ''
  inputText.value = `${existing}${sep}${text}`
  voiceUsedSinceLastSend.value = true
  nextTick(() => {
    autoGrowInput()
    inputRef.value?.focus()
  })
}

const voice = useVoiceTranscription({
  onTranscript: appendTranscript,
  // Admins see the mic even inside the App Store wrapper (embedded web view)
  // so RM staff can run the controlled wrapper-mic-permission test on prod.
  // Members stay guarded until that test passes (a permission-less wrapper
  // TERMINATES the app on mic access).
  allowEmbedded: computed(() => myUser.value?.userLevel === 'admin'),
})
const voiceState = voice.state
const voiceError = voice.error
const voiceLevels = voice.levels

const micRecording = computed(() => voiceState.value === 'recording')
const micTranscribing = computed(() => voiceState.value === 'transcribing')
const micBusy = computed(() => voiceState.value === 'requesting' || voiceState.value === 'transcribing')

const micElapsed = computed(() => {
  const s = Math.floor(voice.elapsedMs.value / 1000)

  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
})

const voiceErrorText = computed(() => {
  if (voiceState.value !== 'error') return ''
  if (voiceError.value === 'mic_denied') return 'Microphone access is needed for voice input.'
  if (voiceError.value === 'empty_transcript') return "Didn't catch any speech — please try again."

  return "Couldn't transcribe that — please try again."
})

// A cleared composer means any earlier transcript was discarded by the member.
watch(inputText, v => {
  if (!v) voiceUsedSinceLastSend.value = false
  if (voiceState.value === 'error') voice.reset()
})

// One-time RM-voiced explainer BEFORE the OS microphone prompt (the OS dialog
// names the raw domain and can't be rebranded — this sets expectations first).
// Continue is a user gesture, so getUserMedia can start directly from it.
const VOICE_PREPROMPT_KEY = 'rmVoicePrepromptSeen'
const voicePrepromptOpen = ref(false)

function voicePrepromptSeen() {
  try {
    return !!localStorage.getItem(VOICE_PREPROMPT_KEY)
  } catch (e) {
    return true // storage unavailable (private mode) — don't gate the mic
  }
}

function handleMicTap() {
  if (!voicePrepromptSeen()) {
    voicePrepromptOpen.value = true

    return
  }
  voice.start()
}

function acceptVoicePreprompt() {
  try {
    localStorage.setItem(VOICE_PREPROMPT_KEY, '1')
  } catch (e) { /* private mode — show it again next time, harmless */ }
  voicePrepromptOpen.value = false
  voice.start()
}

function handleNewConversation() {
  // Explicit: the member asked for this blank chat, so RM AI stays in it until
  // they send something or open one from history.
  startNewConversation({ explicit: true })
  showHistory.value = false
}

function handleLoadConversation(id) {
  loadConversation(id)
  showHistory.value = false
}

// "Review & Submit Request" CTA tapped in a chat message. Open the review
// dialog as an overlay over the AI screen (step 7a). Unlike the legacy auto-
// handoff, we do NOT navigate to /bookings on close — the member returns to the
// chat with history intact, where the agent acknowledges the request's status
// on their next message.
function handleOpenReservationRequest(reservationId) {
  if (!reservationId) return
  reservationsStore.openReservationDialogAtStep(reservationId, '7a')
}

function handleToggleHistory() {
  showHistory.value = !showHistory.value
  if (showHistory.value) {
    listConversations()
  } else {
    historySearch.value = ''
  }
}

function handleShowTrace(msgDbIndex) {
  traceMsgDbIndex.value = msgDbIndex
  traceModalOpen.value = true
}

function handleSelectOption(text) {
  if (isProcessing.value) return
  sendMessage(text, { inputMethod: 'chip' })
}

// Starter chips shown in the launcher (sheet) empty state. Tapping one seeds a
// prompt to the agent and expands the sheet to full height so the conversation
// has room. "Request reservation" feeds the agent's existing reservation
// handoff; "Add to Profile" (was "Add favorite(s)", Kapil 2026-09-16) opens the Profile flow — favorites, notes,
// Profile fields & social links, Verified Status requests.
// RM-280: four starters, one per pillar, equal weight (no pre-selected primary,
// no hints — the label is the whole message, verb over object on two lines). Request reservation leads: it is
// the core use case and availability surfaces inside it too. "Check
// availability" appears only where the feature is on (server flag via
// GET /agent/access) — never in prod until approved.
const launcherSuggestions = computed(() => {
  // Kapil, 2026-09-16: a tile's text is never echoed as a bubble; the server answers every tile itself with
  // "Hi {FirstName} - we'd love to help you …!" and the flow's question (lib/agent/starters.js keys on these exact texts).
  const items = [{ label: 'Request\nreservation', prompt: 'Request a reservation', inputMethod: 'starter' }]
  if (accessStatus.value?.publicAvailability) {
    items.push({ label: 'Check\navailability', prompt: 'Check availability', inputMethod: 'starter' })
  }
  items.push(
    // Kapil, 2026-09-15: "Discover", ahead of Add to Profile.
    { label: 'Discover', prompt: 'Discover', inputMethod: 'starter' },
    { label: 'Add to\nProfile', prompt: 'Add to Profile', inputMethod: 'starter' },
  )

  return items
})

function handleSuggestion(prompt, inputMethod) {
  if (isProcessing.value) return
  if (props.sheetMode) emit('expand')
  sendMessage(prompt, inputMethod ? { inputMethod } : {})
}

function expandSheet() {
  emit('expand')
}

function handleSwitchAgent(type) {
  switchAgentType(type)
  showHistory.value = false
}

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

// Extra scrollable room below the messages so a newly-sent user message can
// be pinned to the top of the visible area while the agent is responding.
const bottomSpacerHeight = ref(0)

// Pin a message element to the top of the visible area (padding the bottom so
// there is room to scroll it all the way up).
function pinMessageToTop(el) {
  if (!messagesContainer.value || !el) return
  const containerHeight = messagesContainer.value.clientHeight
  bottomSpacerHeight.value = Math.max(0, containerHeight - el.offsetHeight - 32)

  nextTick(() => {
    if (!messagesContainer.value) return
    const containerRect = messagesContainer.value.getBoundingClientRect()
    const msgRect = el.getBoundingClientRect()
    const offset = msgRect.top - containerRect.top - 12
    messagesContainer.value.scrollTo({
      top: messagesContainer.value.scrollTop + offset,
      behavior: 'smooth',
    })
  })
}

function scrollNewUserMessageToTop() {
  nextTick(() => {
    if (!messagesContainer.value) return
    const userMessages = messagesContainer.value.querySelectorAll('.agent-message--user')
    pinMessageToTop(userMessages[userMessages.length - 1])
  })
}

// A chip's reply (often a long list) starts at the top of the view, so the
// member reads down from its first line instead of landing at its end
// (Kapil, 2026-09-15: "All" dropped me to the bottom).
function scrollNewAssistantMessageToTop() {
  nextTick(() => {
    if (!messagesContainer.value) return
    const assistantMessages = messagesContainer.value.querySelectorAll('.agent-message--assistant')
    pinMessageToTop(assistantMessages[assistantMessages.length - 1])
  })
}

let prevMessageCount = messages.value.length

watch(messages, newMessages => {
  const newCount = newMessages.length
  const lastMsg = newMessages[newCount - 1]
  const diff = newCount - prevMessageCount
  prevMessageCount = newCount

  if (diff === 1 && lastMsg?.role === 'user' && lastMsg.chip) {
    // A tapped chip has no bubble to pin (it fills on the reply above) — pinning
    // would find the OLDER member bubble and scroll back up to it (Kapil,
    // 2026-09-15). Leave the view where it is; the reply lands within a second.
  } else if (diff === 1 && lastMsg?.role === 'assistant' && newMessages[newCount - 2]?.chip) {
    // The chip's reply: pin its first line to the top so a long list is read
    // from the start, not from its end.
    scrollNewAssistantMessageToTop()
  } else if (diff === 1 && lastMsg?.role === 'user') {
    // New user message — pin it to the top so the typing dots sit beneath it
    scrollNewUserMessageToTop()
  } else if (diff > 1 || newCount === 0) {
    // Conversation loaded or cleared — reset spacer and jump to the latest
    bottomSpacerHeight.value = 0
    if (newCount > 0) scrollToBottom()
  }

  // Otherwise (single agent message added or content tweak): leave scroll
  // position alone so the pinned user message stays at the top.
}, { deep: true })

// Re-check access once the user logs in (e.g. completed join/login from the gate)
watch(isLoggedIn, async loggedIn => {
  if (loggedIn && (props.pageMode || props.sheetMode) && !accessStatus.value) {
    await checkAccess()
    if (accessStatus.value?.access && !connected.value) {
      connect()
    }
  }
})

// Auto-focus input when chat opens
watch(isOpen, open => {
  if (open) {
    nextTick(() => {
      inputRef.value?.focus()
    })
  }
})

// iOS keyboard: resize and reposition container to match visual viewport
const keyboardOpen = ref(false)

function handleViewportChange() {
  // Only manage viewport sizing in full-screen page mode. In sheet mode the
  // host bottom-sheet owns layout, so we must not resize/reposition here.
  if (!smAndDown.value || !agentContentRef.value || !props.pageMode) return
  const vv = window.visualViewport
  if (!vv) return

  // Size the interactive area to the visual viewport
  agentContentRef.value.style.height = `${vv.height}px`
  agentContentRef.value.style.top = `${vv.offsetTop}px`
  keyboardOpen.value = vv.height < window.innerHeight - 50
  scrollToBottom()
}

// Sync activeAgentType to the prop eagerly so the right system prompt/UI is
// used from the very first render. The backend re-validates admin access on
// every message, so it's safe to flip the client state optimistically.
if (props.agentType && activeAgentType.value !== props.agentType) {
  switchAgentType(props.agentType)
}

// Also react to prop changes (e.g. navigating between /ai and /ai/admin
// without unmounting the singleton state).
watch(() => props.agentType, type => {
  if (type && activeAgentType.value !== type) {
    switchAgentType(type)
  }
})

onMounted(async () => {
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', handleViewportChange)
    window.visualViewport.addEventListener('scroll', handleViewportChange)
  }

  // In page mode (and the sheet launcher), auto-initialize the chat. Clear any
  // stale singleton state immediately, then let resumeOrStartConversation decide
  // (once connected) whether to resume the member's most recent conversation
  // (active within the last 12h) or start fresh. An explicit New Chat overrides.
  if (props.pageMode || props.sheetMode) {
    startNewConversation()
    // RM-359: a link back into one chat (/ai?chat=<id>) opens it instead of the usual resume.
    if (props.pageMode) {
      const linked = linkedChatId(route.query)

      if (linked)
        openLinkedConversation(linked)
    }
    resumeOrStartConversation()
    hasBeenOpened.value = true
    isOpen.value = true
    if (isLoggedIn.value && !accessStatus.value) {
      await checkAccess()
    }
    if (accessStatus.value?.access && !connected.value) {
      connect()
    }
  }
})

onBeforeUnmount(() => {
  if (window.visualViewport) {
    window.visualViewport.removeEventListener('resize', handleViewportChange)
    window.visualViewport.removeEventListener('scroll', handleViewportChange)
  }

  // Reset open state when the page-mode / sheet component unmounts so it
  // doesn't leak into other pages or tabs that reuse the singleton composable
  // state.
  if (props.pageMode || props.sheetMode) {
    isOpen.value = false
  }
})

function formatDate(dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const now = new Date()
  const diff = now - date
  const mins = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 7) return `${days}d ago`

  return date.toLocaleDateString()
}

// iMessage-style timestamp separators: shown before the first message, and
// between messages when the gap exceeds the threshold.
const TIMESTAMP_GAP_MS = 15 * 60 * 1000

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()
}

function formatMessageTimestamp(isoString) {
  if (!isoString) return ''
  const date = new Date(isoString)
  if (Number.isNaN(date.getTime())) return ''

  const now = new Date()
  const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

  if (isSameDay(date, now)) {
    return `Today ${time}`
  }

  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (isSameDay(date, yesterday)) {
    return `Yesterday ${time}`
  }

  const diffDays = Math.floor((now - date) / 86400000)
  if (diffDays < 7) {
    const weekday = date.toLocaleDateString([], { weekday: 'long' })
    
    return `${weekday} ${time}`
  }

  const sameYear = date.getFullYear() === now.getFullYear()
  const datePart = date.toLocaleDateString([], sameYear
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' })
  
  return `${datePart} ${time}`
}

function shouldShowTimestamp(message, prevMessage) {
  if (!message?.timestamp) return false
  if (!prevMessage) return true
  if (!prevMessage.timestamp) return false
  const gap = new Date(message.timestamp) - new Date(prevMessage.timestamp)
  
  return gap >= TIMESTAMP_GAP_MS
}

function formatBubbleTime(isoString) {
  if (!isoString) return ''
  const date = new Date(isoString)
  if (Number.isNaN(date.getTime())) return ''

  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

// Swipe-left to reveal per-message timestamps (iMessage-style)
const MAX_DRAG = 68
const DRAG_LOCK_THRESHOLD = 8
const dragOffset = ref(0)
const isDragging = ref(false)
const dragState = ref(null)

const dragLayerStyle = computed(() => ({
  transform: `translate3d(${dragOffset.value}px, 0, 0)`,
  transition: isDragging.value ? 'none' : 'transform 0.25s ease',
}))

function onDragPointerDown(e) {
  if (e.button !== undefined && e.button > 0) return
  dragState.value = { startX: e.clientX, startY: e.clientY, axis: null, pointerId: e.pointerId }
}

function onDragPointerMove(e) {
  const state = dragState.value
  if (!state) return

  const dx = e.clientX - state.startX
  const dy = e.clientY - state.startY

  if (!state.axis) {
    if (Math.abs(dx) < DRAG_LOCK_THRESHOLD && Math.abs(dy) < DRAG_LOCK_THRESHOLD) return

    // Only engage the swipe when the gesture is predominantly horizontal-left.
    if (Math.abs(dx) > Math.abs(dy) && dx < 0) {
      state.axis = 'x'
      isDragging.value = true
      try {
        e.currentTarget?.setPointerCapture?.(e.pointerId)
      } catch {
        // ignore — capture is best-effort
      }
    } else {
      dragState.value = null

      return
    }
  }

  if (state.axis === 'x') {
    if (e.cancelable) e.preventDefault()
    dragOffset.value = Math.max(-MAX_DRAG, Math.min(0, dx))
  }
}

function onDragPointerUp(e) {
  const state = dragState.value
  if (state?.axis === 'x') {
    try {
      e.currentTarget?.releasePointerCapture?.(state.pointerId)
    } catch {
      // ignore
    }
  }
  if (isDragging.value) {
    dragOffset.value = 0
    isDragging.value = false
  }
  dragState.value = null
}

function handleClose() {
  if (props.sheetMode) {
    // The host bottom-sheet owns dismissal.
    emit('close')

    return
  }
  if (props.pageMode) {
    // If the user navigated directly to /ai (no history), router.back() does nothing
    // and traps them on the page. Use a safe fallback.
    if (window.history.length > 1) {
      router.back()
    } else {
      router.push('/')
    }
  } else {
    closeChat()
  }
}
</script>

<template>
  <div
    v-if="pageMode || sheetMode"
    ref="agentContentRef"
    class="agent-content"
    :class="{
      'agent-content--fullscreen': pageMode,
      'agent-content--page': pageMode,
      'agent-content--sheet': sheetMode,
      'agent-content--mobile': pageMode && smAndDown,
      'agent-content--keyboard-open': pageMode && keyboardOpen,
    }"
  >
    <!-- Header: the teal band stays full-bleed; its contents sit in the same 720px column as the chat -->
    <div class="agent-header px-4 py-3">
      <div class="agent-header__inner d-flex align-center ga-2">
        <VBtn
          v-if="accessStatus?.access"
          icon="mdi-menu"
          variant="text"
          size="small"
          color="white"
          @click="handleToggleHistory"
        />
        <div class="agent-header__brand d-flex align-center ga-2 flex-grow-1">
          <div class="agent-header__avatar">
            <VIcon
              icon="mdi-sparkles"
              size="18"
              color="primary-darker"
            />
          </div>
          <span class="agent-header__title">
            {{ activeAgentType === 'admin' ? 'RM Team' : 'RM AI' }}
            <span class="agent-header__beta">Beta</span>
          </span>
        </div>
        <VBtn
          v-if="accessStatus?.access"
          icon="mdi-square-edit-outline"
          variant="text"
          size="small"
          color="white"
          aria-label="New chat"
          @click="handleNewConversation"
        />
        <VBtn
          v-if="sheetMode && accessStatus?.access"
          icon="mdi-arrow-expand"
          variant="text"
          size="small"
          color="white"
          aria-label="Expand RM AI"
          @click="expandSheet"
        />
        <VBtn
          icon="mdi-close"
          variant="text"
          size="small"
          color="white"
          @click="handleClose"
        />
      </div>
    </div>

    <!-- Reservation summary card -->
    <AgentReservationSummary
      v-if="accessStatus?.access"
      :restaurant="confirmedRestaurant"
      :booking-member="confirmedBookingMember"
      :summary="reservationSummary"
      :reservation-draft-id="reservationDraftId"
    />

    <!-- RM-398: a group text thread, read in the app: who is on it -->
    <div
      v-if="groupThread"
      class="agent-group-strip"
    >
      <VIcon
        icon="mdi-account-multiple-outline"
        size="16"
        class="agent-group-strip__icon"
      />
      <div class="agent-group-strip__text">
        <span class="agent-group-strip__label">Group text</span>
        <span
          v-if="groupPeopleLine"
          class="agent-group-strip__people"
        >with {{ groupPeopleLine }}</span>
      </div>
    </div>

    <!-- Not logged in gate -->
    <div
      v-if="!isLoggedIn"
      class="agent-gate d-flex flex-column align-center justify-center flex-grow-1 pa-6"
    >
      <VIcon
        icon="mdi-sparkles"
        size="48"
        color="primary-dark"
        class="mb-4"
      />
      <div class="text-body-1 text-medium-emphasis text-center mb-6">
        Feature for Platinum members
      </div>
      <VBtn
        color="primary-darker"
        variant="flat"
        @click="handleRequestToJoin"
      >
        Request to join
      </VBtn>
    </div>

    <!-- Access loading state -->
    <div
      v-else-if="accessLoading"
      class="agent-gate d-flex flex-column align-center justify-center flex-grow-1"
    >
      <VProgressCircular
        indeterminate
        color="primary-dark"
        size="32"
      />
    </div>

    <!-- Non-Platinum gate -->
    <div
      v-else-if="accessStatus && !accessStatus.access && accessStatus.reason === 'not_platinum'"
      class="agent-gate d-flex flex-column align-center justify-center flex-grow-1 pa-6"
    >
      <VIcon
        icon="mdi-sparkles"
        size="48"
        color="primary-dark"
        class="mb-4"
      />
      <div class="text-body-1 text-medium-emphasis text-center mb-6">
        Feature for Platinum members
      </div>
      <VBtn
        color="primary-darker"
        variant="flat"
        @click="handleRequestPlatinum"
      >
        Request Platinum
      </VBtn>
    </div>

    <!-- Suspended gate -->
    <div
      v-else-if="accessStatus && !accessStatus.access && accessStatus.reason === 'suspended'"
      class="agent-gate d-flex flex-column align-center justify-center flex-grow-1 pa-6"
    >
      <VIcon
        icon="mdi-account-cancel-outline"
        size="48"
        color="grey"
        class="mb-4"
      />
      <div class="text-body-1 text-medium-emphasis text-center">
        Your access to RM AI has been suspended
      </div>
    </div>

    <!-- Main chat content (only when access granted) -->
    <template v-else-if="accessStatus?.access">
      <!-- Content area -->
      <div class="agent-body flex-grow-1">
        <!-- Messages Area (always visible) -->
        <div
          ref="messagesContainer"
          class="agent-messages pa-3"
          @pointerdown="onDragPointerDown"
          @pointermove="onDragPointerMove"
          @pointerup="onDragPointerUp"
          @pointercancel="onDragPointerUp"
        >
          <!-- Welcome message when empty -->
          <div
            v-if="messages.length === 0 && !streamingContent && !isTyping && !inputText.trim()"
            class="agent-welcome d-flex flex-column align-center"
          >
            <!--
              <div class="text-h6 font-weight-medium mb-3 text-center">
              Welcome {{userFirstName}}
              </div>
            -->
            <div
              v-if="activeAgentType === 'admin'"
              id="welcome-message"
              class="text-body-2 text-medium-emphasis text-center mb-2"
              style="max-width: 360px; line-height: 1.6; font-size: 16px;"
            >
              <p>
                <b>What would you like done?</b>
              </p>
              <p class="mt-3">
                <strong>Examples:</strong>
              </p>
              <p>
                &ldquo;What needs me right now?&rdquo;
              </p>
              <p>
                &ldquo;What contacts do we have for NAIA?&rdquo;
              </p>
              <p>
                &ldquo;Change NAIA's Fri &amp; Sat dinner minimum to $250 per person&rdquo;
              </p>
              <p>
                &ldquo;Confirm Justin's request at NAIA for 8pm, confirmed by Sarah&rdquo;
              </p>
              <p>
                &ldquo;Approve the membership applications from this week&rdquo;
              </p>
              <p class="mt-3 text-caption">
                Every change is read back first and made only when you reply yes.
              </p>
            </div>
            <div
              v-else
              id="welcome-message"
              class="text-body-2 text-medium-emphasis text-center mb-2"
              style="max-width: 340px; line-height: 1.6; font-size: 16px;"
            >
              <p class="launcher-greeting">
                Hi {{ userFirstName }}
              </p>

              <!-- Starters: one per pillar, same in the launcher and on the full /ai page (RM-280) -->
              <div
                class="launcher-chips"
                role="list"
              >
                <button
                  v-for="s in launcherSuggestions"
                  :key="s.label"
                  type="button"
                  class="launcher-chip"
                  role="listitem"
                  :disabled="!connected || isProcessing"
                  @click="handleSuggestion(s.prompt, s.inputMethod)"
                >
                  {{ s.label }}
                </button>
              </div>
              <!--
                Why the tiles are dead. Without this the launcher just looks
                broken while the socket comes back (Kapil, 2026-09-22).
              -->
              <p
                v-if="!connected"
                class="launcher-reconnecting"
              >
                Reconnecting…
              </p>
            </div>
          </div>

          <div
            class="agent-messages-drag"
            :class="{ 'agent-messages-drag--active': isDragging }"
            :style="dragLayerStyle"
          >
            <!-- Message bubbles -->
            <template
              v-for="(msg, idx) in messages"
              :key="idx"
            >
              <div
                v-if="!msg.chip && !msg.hidden && shouldShowTimestamp(msg, messages[idx - 1])"
                class="agent-message-timestamp"
              >
                {{ formatMessageTimestamp(msg.timestamp) }}
              </div>
              <!-- A tapped chip is shown filled on the reply above, not as a bubble -->
              <AgentChatMessage
                v-if="!msg.chip && !msg.hidden"
                :role="msg.role"
                :selected-option="messages[idx + 1]?.chip ? messages[idx + 1].content : null"
                :content="msg.content"
                :attached-image-id="msg.attachedImageId || null"
                :attached-file-type="msg.attachedFileType || null"
                :is-admin="isAdmin"
                :msg-db-index="msg.msgDbIndex"
                :has-trace="msg.hasTrace"
                :channel="msg.channel || 'web'"
                :support-reply="!!msg.supportReply"
                :actor="msg.actor || null"
                :sender-name="msg.senderName || ''"
                :from-other="!!msg.fromOther"
                :conversation-id="groupThread ? null : conversationId"
                :bubble-time="formatBubbleTime(msg.timestamp)"
                :stopped="msg.stopped"
                :regenerated="msg.regenerated"
                :show-regenerate="!groupThread && idx === lastAssistantIdx && !isProcessing && connected"
                :chips-active="!groupThread && idx === messages.length - 1 && !isProcessing && connected"
                @show-trace="handleShowTrace"
                @select-option="handleSelectOption"
                @open-reservation-request="handleOpenReservationRequest"
                @regenerate="regenerate"
                @open-support="supportDialogOpen = true"
                @open-help="helpDialogOpen = true"
              />
            </template>

            <!-- Tool status indicator -->
            <AgentChatToolStatus
              v-if="currentToolCall"
              :display-name="currentToolCall.displayName"
            />

            <!--
              Streaming message. Same handlers as a saved reply: the Review &
              Submit button can appear while the reply is still streaming, and a
              tap then must open the review dialog too (RM-328, 2026-09-28: the
              E2E run clicked it mid-stream and nothing happened).
            -->
            <AgentChatMessage
              v-if="streamingContent"
              role="assistant"
              :content="streamingContent"
              @select-option="handleSelectOption"
              @open-reservation-request="handleOpenReservationRequest"
              @open-support="supportDialogOpen = true"
              @open-help="helpDialogOpen = true"
            />

            <!-- Typing indicator -->
            <div
              v-if="isTyping && !streamingContent && !currentToolCall"
              class="agent-typing d-flex align-center ga-1 pa-2"
            >
              <span class="agent-typing__dot" />
              <span class="agent-typing__dot" />
              <span class="agent-typing__dot" />
            </div>
          </div>

          <!-- Spacer so the latest user message can scroll to the top of the viewport -->
          <div
            v-if="bottomSpacerHeight > 0"
            class="agent-messages-spacer"
            :style="{ height: `${bottomSpacerHeight}px` }"
            aria-hidden="true"
          />
        </div>
      </div>

      <!--
        Connection failure banner: a dead socket must never look like
        "nothing happening" (dev incident 2026-07-16: unregistered /agent
        namespace was invisible to members and admins alike). 
      -->
      <div
        v-if="!connected && error"
        class="agent-connection-error mx-3 mb-1"
      >
        <span>RM AI can't connect right now.</span>
        <button
          class="agent-connection-error__retry"
          @click="connect"
        >
          Retry
        </button>
      </div>

      <!--
        RM-398: a group text thread is read here and continued on the phone.
        No composer: a message typed here could not reach the others as this
        member. One action: back to the thread.
      -->
      <div
        v-if="groupThread"
        class="agent-group-foot px-4 pb-4 pt-3"
      >
        <p class="agent-group-foot__line">
          <template v-if="groupThread.onOwnPhone || !groupThread.onBehalfBy">
            This conversation continues in your group text.
          </template>
          <template v-else>
            {{ groupThread.onBehalfBy }} is on this group text for you. It continues by text.
          </template>
        </p>
        <VBtn
          v-if="groupTextHref"
          :href="groupTextHref"
          color="primary"
          variant="flat"
          block
          size="large"
          prepend-icon="mdi-message-text-outline"
          class="agent-group-foot__open"
        >
          Open group text
        </VBtn>
      </div>

      <!-- Input Area -->
      <div
        v-else
        class="agent-input px-3 pb-3 pt-2"
      >
        <!-- Pending attachment: image thumbnail, or a document chip for a PDF -->
        <div
          v-if="pendingAttachment"
          class="agent-attach-chip"
        >
          <div
            v-if="pendingAttachment.isPdf"
            class="agent-attach-thumb agent-attach-thumb--pdf"
            :title="pendingAttachment.name"
          >
            <VIcon
              size="32"
              icon="mdi-file-pdf-box"
            />
          </div>
          <img
            v-else
            :src="pendingAttachment.previewUrl"
            class="agent-attach-thumb agent-attach-thumb--zoomable"
            alt="Attached image — tap to view full size"
            @click="attachZoomOpen = true"
          >
          <VProgressCircular
            v-if="pendingAttachment.uploading"
            class="agent-attach-spinner"
            size="16"
            width="2"
            indeterminate
          />
          <button
            class="agent-attach-remove"
            aria-label="Remove attachment"
            @click="removeAttachment"
          >
            <VIcon
              size="14"
              icon="mdi-close"
            />
          </button>
        </div>

        <!-- The attachment full size, before it is sent — the same dialog as a sent image. -->
        <VDialog
          v-if="pendingAttachment && !pendingAttachment.isPdf"
          v-model="attachZoomOpen"
          max-width="720"
        >
          <VCard class="agent-attach-zoom">
            <img
              :src="pendingAttachment.previewUrl"
              alt="Attached image, full size"
            >
            <div class="agent-attach-zoom__actions">
              <VBtn
                variant="text"
                size="small"
                @click="attachZoomOpen = false"
              >
                Close
              </VBtn>
            </div>
          </VCard>
        </VDialog>
        <div class="agent-input-box">
          <textarea
            ref="inputRef"
            v-model="inputText"
            :placeholder="!connected ? 'Connecting...' : (messages.length ? 'Message RM AI…' : 'What would you like to do today?')"
            class="agent-input-textarea"
            rows="1"
            @keydown="handleKeydown"
            @input="autoGrowInput"
          />
          <div class="agent-input-actions">
            <!--
              Recording (RM-163 UI v2): Cancel · live waveform (real mic
              levels) · timer · Stop · Send — the moving bars are the
              "listening" indicator and exist ONLY while the mic is live.
              Stop and ↑ both finish the take and transcribe into the
              composer for review (never auto-send); Cancel discards.
            -->
            <template v-if="micRecording">
              <button
                class="agent-mic-cancel"
                aria-label="Cancel recording"
                @click="voice.cancel()"
              >
                <VIcon
                  size="18"
                  icon="mdi-close"
                />
              </button>
              <div
                class="agent-voice-wave"
                aria-hidden="true"
              >
                <span
                  v-for="(lvl, i) in voiceLevels"
                  :key="i"
                  class="agent-voice-bar"
                  :style="{ height: `${4 + Math.round(lvl * 18)}px` }"
                />
              </div>
              <span class="agent-mic-timer">{{ micElapsed }}</span>
              <button
                class="agent-mic-btn"
                aria-label="Stop recording and transcribe"
                @click="voice.stop()"
              >
                <VIcon
                  size="18"
                  icon="mdi-stop"
                />
              </button>
              <button
                class="agent-send-btn"
                aria-label="Finish recording and transcribe"
                @click="voice.stop()"
              >
                <VIcon
                  size="18"
                  icon="mdi-arrow-up"
                />
              </button>
            </template>
            <template v-else>
              <span
                v-if="micTranscribing"
                class="agent-mic-timer"
              >Transcribing…</span>
              <!--
                Attach (receipts, profile photos): opens the file picker for
                images and PDFs; hidden while a reply is generating, like the mic.
              -->
              <button
                v-if="!isProcessing"
                class="agent-mic-btn"
                :disabled="!connected || pendingAttachment?.uploading"
                aria-label="Attach a photo or PDF"
                title="Attach a photo or PDF"
                @click="handleAttachTap"
              >
                <VIcon
                  size="18"
                  icon="mdi-image-outline"
                />
              </button>
              <!--
                Mic (RM-163): hidden while a reply is generating and where
                recording isn't supported; spinner while requesting the mic
                or transcribing.
              -->
              <button
                v-if="voice.isSupported.value && !isProcessing"
                class="agent-mic-btn"
                :disabled="micBusy || !connected"
                aria-label="Dictate"
                title="Dictate"
                @click="handleMicTap"
              >
                <VProgressCircular
                  v-if="micBusy"
                  size="16"
                  width="2"
                  indeterminate
                />
                <VIcon
                  v-else
                  size="18"
                  icon="mdi-microphone-outline"
                />
              </button>
              <!--
                While a reply is generating, the send control becomes Stop:
                same spot, one action, aborts the turn server-side.
              -->
              <button
                v-if="isProcessing"
                class="agent-send-btn"
                :disabled="stopRequested || !connected"
                aria-label="Stop generating"
                @click="stopGeneration"
              >
                <VProgressCircular
                  v-if="stopRequested"
                  size="16"
                  width="2"
                  indeterminate
                />
                <VIcon
                  v-else
                  size="18"
                  icon="mdi-stop"
                />
              </button>
              <button
                v-else
                class="agent-send-btn"
                :disabled="(!inputText.trim() && !pendingAttachment?.imageId) || pendingAttachment?.uploading || !connected"
                aria-label="Send message"
                @click="handleSend"
              >
                <VIcon
                  size="18"
                  icon="mdi-arrow-up"
                />
              </button>
            </template>
          </div>
        </div>
        <p
          v-if="voiceErrorText"
          class="agent-voice-error"
        >
          {{ voiceErrorText }}
        </p>
        <p
          v-if="attachError"
          class="agent-voice-error"
        >
          {{ attachError }}
        </p>
        <input
          ref="attachInputRef"
          type="file"
          accept="image/*,application/pdf"
          class="d-none"
          @change="handleAttachFile"
        >
      </div>

      <!-- One-time voice explainer (RM-163) — shown before the OS mic prompt -->
      <VDialog
        v-model="voicePrepromptOpen"
        max-width="360"
      >
        <VCard class="agent-voice-preprompt">
          <VCardText class="agent-voice-preprompt-text">
            RM uses your microphone to transcribe your request. You'll review the text before anything is sent.
          </VCardText>
          <VCardActions class="agent-voice-preprompt-actions">
            <VSpacer />
            <VBtn
              variant="text"
              size="small"
              @click="voicePrepromptOpen = false"
            >
              Not now
            </VBtn>
            <VBtn
              color="primary"
              variant="flat"
              size="small"
              rounded="lg"
              @click="acceptVoicePreprompt"
            >
              Continue
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>

      <!-- History drawer overlay (covers body + input) -->
      <Transition name="history-drawer">
        <div
          v-if="showHistory"
          class="agent-history-overlay"
          @click.self="handleToggleHistory"
        >
          <div class="agent-history-drawer">
            <div class="agent-history-drawer__header d-flex align-center ga-1 pa-3">
              <VTextField
                v-model="historySearch"
                placeholder="Search chats..."
                variant="solo-filled"
                style="margin-bottom: 0 !important"
                flat
                rounded="pill"
                density="compact"
                hide-details
                prepend-inner-icon="mdi-magnify"
                bg-color="grey-lighten-3"
                class="flex-grow-1 agent-history-search"
                autofocus
              />
              <VBtn
                icon="mdi-square-edit-outline"
                variant="text"
                size="default"
                class="align-self-center"
                @click="handleNewConversation"
              />
            </div>
            <VDivider />
            <div class="agent-history-drawer__list pa-3">
              <div class="text-caption font-weight-bold text-medium-emphasis mb-2">
                Your chats
              </div>

              <!-- Loading state -->
              <div
                v-if="conversationsLoading"
                class="d-flex align-center justify-center ga-2 py-4"
              >
                <VProgressCircular
                  indeterminate
                  size="20"
                  width="2"
                  color="primary-dark"
                />
                <span class="text-body-2 text-medium-emphasis">Loading chats...</span>
              </div>

              <!-- Empty state (only when not loading) -->
              <div
                v-else-if="filteredConversations.length === 0"
                class="text-body-2 text-medium-emphasis"
              >
                {{ historySearch ? 'No matching chats.' : 'No conversations yet.' }}
              </div>

              <VList
                v-else
                density="compact"
              >
                <VListItem
                  v-for="conv in filteredConversations"
                  :key="conv._id"
                  :active="conversationId === conv._id"
                  @click="handleLoadConversation(conv._id)"
                >
                  <VListItemTitle class="text-body-2">
                    {{ conv.title }}
                  </VListItemTitle>
                  <VListItemSubtitle class="text-caption">
                    <template v-if="conv.group">
                      Group text ·
                    </template>
                    {{ formatDate(conv.lastActivityAt) }}
                  </VListItemSubtitle>
                </VListItem>
              </VList>
            </div>
          </div>
        </div>
      </Transition>
    </template>
    <!-- Trace modal (admin only) -->
    <AgentTraceModal
      v-if="isAdmin"
      v-model="traceModalOpen"
      :conversation-id="conversationId"
      :msg-db-index="traceMsgDbIndex"
    />

    <!-- In-place support chat (RM-212) -->
    <SupportChatDialog v-model="supportDialogOpen" />

    <AgentHelpDialog
      v-model="helpDialogOpen"
      @message-support="supportDialogOpen = true"
    />

    <!-- Upgrade to Platinum dialog -->
    <UpgradeProfileDialog
      v-model="showUpgradeDialog"
      :product-id="upgradeProductId"
      :price-dollars="upgradePriceDollars"
      @complete="onUpgradeDialogComplete"
      @cancel="onUpgradeDialogCancel"
    />
  </div>
</template>

<style scoped>
/* RM-398 — a group text thread, read in the app. The strip says what this is
   and who is on it; the foot is the one action (back to the thread). */
.agent-group-strip {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid var(--ds-border-light, rgba(0, 0, 0, 0.08));
  background: var(--ds-surface-inset, #f6f5f3);
}

.agent-group-strip__icon {
  margin-top: 2px;
  color: var(--ds-text-muted, rgba(0, 0, 0, 0.6));
}

.agent-group-strip__text {
  min-width: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--ds-text-body, rgba(0, 0, 0, 0.74));
}

.agent-group-strip__label {
  font-weight: 600;
  color: var(--ds-text-strong, rgba(0, 0, 0, 0.87));
  margin-right: 4px;
}

.agent-group-strip__people {
  overflow-wrap: anywhere;
}

.agent-group-foot {
  border-top: 1px solid var(--ds-border-light, rgba(0, 0, 0, 0.08));
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
}

.agent-group-foot__line {
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 20px;
  color: var(--ds-text-body, rgba(0, 0, 0, 0.74));
  text-align: center;
}

.agent-content {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

/* Page mode: fills the entire viewport without a dialog wrapper */
.agent-content--page {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
}

/* Fullscreen layout */
.agent-content--fullscreen {
  height: 100dvh;
  overflow: hidden;
}

/* Sheet layout: fills the host bottom-sheet rather than the viewport */
.agent-content--sheet {
  height: 100%;
  overflow: hidden;
}

/* Premium background — a warm near-white with a soft, restrained brand-teal
   glow rising from the bottom (ambient, not a spotlight; no blue). Shared by
   the full-screen page and the launcher sheet so RM AI reads consistently.
   The glow is sized in VIEWPORT units (vw/vh) and anchored to the element's
   bottom edge — which is the viewport bottom for both the full page and the
   bottom sheet — so it renders at the same physical size regardless of the
   container height (78vh launcher vs 100vh page). Inner surfaces stay
   transparent so the glow shows through; message/input cards keep their own
   white so they lift off the background. */
.agent-content--fullscreen,
.agent-content--sheet {
  background:
    radial-gradient(120vw 78vh at 50% 100%, rgba(69, 181, 169, 0.14) 0%, rgba(69, 181, 169, 0.05) 40%, rgba(69, 181, 169, 0) 66%),
    #fbfbf9;
}

/* Mobile: fixed positioning prevents iOS keyboard scroll */
.agent-content--mobile {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

/* Fills the gap between the visual viewport and screen bottom when iOS keyboard is open */
.agent-content--mobile::after {
  content: '';
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 100vh;
  background: #fbfbf9;
  z-index: -1;
  pointer-events: none;
}

/* Header */
.agent-header {
  flex-shrink: 0;
  background: linear-gradient(135deg, var(--ds-brand-primary-dark) 0%, var(--ds-brand-primary-darker) 100%);
  color: #fff;
}

/* Header contents share the chat's reading column (720px, centered) so the controls line up with the
   messages and the composer on desktop and tablet; on phones the column is the viewport, so nothing moves. */
.agent-header__inner {
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
}

.agent-header__avatar {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
}

.agent-header__title {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.2px;
}

.agent-header__beta {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  padding: 1px 5px;
  border-radius: 4px;
  vertical-align: middle;
  margin-left: 2px;
}

.agent-tabs {
  flex-shrink: 0;
}

.agent-body {
  position: relative;
  overflow: hidden;
  min-height: 0;
}

.agent-messages {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  overflow-x: hidden;
  /* Keep scrolling inside the chat; don't chain to the page/sheet behind it. */
  overscroll-behavior: contain;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Let the browser handle vertical scroll natively; we own horizontal gestures. */
  touch-action: pan-y;
}

.agent-messages > * {
  width: 100%;
  max-width: 720px;
}

/* Drag layer — translated leftward to reveal per-message timestamps.
   Overrides the .agent-messages > * cap so rows always reach the container's
   right edge; the 720px reading cap is applied per-row inside AgentChatMessage
   so the timestamps stay clipped against the container edge at rest. */
.agent-messages > .agent-messages-drag {
  display: flex;
  flex-direction: column;
  max-width: none;
  will-change: transform;
}

.agent-messages-drag--active {
  /* Prevent text selection while the user is swiping */
  user-select: none;
}

.agent-messages-spacer {
  flex-shrink: 0;
  pointer-events: none;
}

/* Welcome state */
.agent-welcome {
  padding: 16px 16px 24px;
  height: 100%;
}

/* Greeting */
.launcher-greeting {
  font-size: 28px;
  font-weight: 500;
  color: #1a1a1a;
  line-height: 1.3;
  text-align: center;
  margin: 0;
}

/* Launcher starter chips: a compact 2×2 grid of equal tiles (all four visible,
   nothing off-screen, nothing pre-selected). Each label is two lines, verb over
   object, so the four tiles read as a set. 16px medium, targets ≥ 64px tall. */
.launcher-chips {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  width: 100%;
  max-width: 320px;
  margin: 28px auto 0;
}

.launcher-chip {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 64px;
  padding: 10px 8px;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.35;
  white-space: pre-line;
  text-align: center;
  border-radius: var(--ds-radius-md, 12px);
  border: 1px solid var(--ds-border-light);
  background: var(--ds-surface-card);
  color: var(--ds-text-primary, inherit);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, transform 0.1s ease;
}

.launcher-chip:hover:not(:disabled) {
  border-color: var(--ds-brand-primary);
  background: var(--ds-brand-primary-light);
}

.launcher-chip:active:not(:disabled) {
  transform: scale(0.98);
}

.launcher-chip:focus-visible {
  outline: none;
  box-shadow: var(--ds-shadow-focus, 0 0 0 3px rgba(15, 110, 86, 0.3));
}

.launcher-reconnecting {
  margin: 10px 0 0;
  font-size: 13px;
  text-align: center;
  color: var(--ds-text-muted, rgba(var(--v-theme-on-surface), 0.6));
}

.launcher-chip:disabled {
  opacity: 0.5;
  cursor: default;
}

/* Gate screens (non-platinum, suspended, beta) — let the premium background show through */
.agent-gate {
  background: transparent;
}

.agent-gate ul {
  padding-left: 20px;
}

.agent-gate li {
  margin-bottom: 4px;
}

/* Suggestion bubbles */
.agent-suggestions {
  flex-shrink: 0;
  padding: 0 12px 8px;
  overflow: hidden;
}

.agent-suggestions__track {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scroll-snap-type: x proximity;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding: 2px 0;
}

.agent-suggestions__track::-webkit-scrollbar {
  display: none;
}

.agent-suggestions__bubble {
  flex-shrink: 0;
  padding: 8px 14px;
  border-radius: 18px;
  border: 1px solid var(--ds-border-light);
  background: var(--ds-surface-card);
  color: var(--ds-text-primary);
  font-size: 13px;
  line-height: 1.3;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.15s ease;
  scroll-snap-align: start;
}

.agent-suggestions__bubble:hover {
  border-color: var(--ds-brand-primary-dark);
  background: var(--ds-brand-primary-light);
  box-shadow: var(--ds-shadow-xs);
}

.agent-suggestions__bubble:active {
  transform: scale(0.97);
}

/* Connection failure banner */
.agent-connection-error {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  max-width: 720px;
  margin: 0 auto;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(var(--v-theme-error), 0.08);
  color: rgb(var(--v-theme-error));
  font-size: 13px;
}

.agent-connection-error__retry {
  font-size: 13px;
  font-weight: 600;
  text-decoration: underline;
  color: inherit;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

/* Input area */
.agent-input {
  flex-shrink: 0;
  background: transparent;
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
}

.agent-content--mobile .agent-input {
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px)) !important;
}

.agent-content--keyboard-open .agent-input {
  padding-bottom: 12px !important;
}

.agent-input-box {
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  border: 1px solid var(--ds-border-light);
  background: var(--ds-surface-card);
  box-shadow: 0 0.25rem 1.25rem rgba(0, 0, 0, 0.035), 0 0 0 0.5px rgba(0, 0, 0, 0.04);
  padding: 14px;
  transition: box-shadow 0.2s ease;
}

.agent-input-box:focus-within {
  box-shadow: 0 0.25rem 1.25rem rgba(0, 0, 0, 0.075), 0 0 0 0.5px var(--ds-brand-primary-dark);
}

.agent-input-textarea {
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  font-size: 16px;
  line-height: 1.5;
  font-family: inherit;
  color: inherit;
  overflow-y: hidden;
  min-height: 24px;
  box-sizing: content-box;
  -webkit-appearance: none;
  appearance: none;
}

.agent-input-textarea::placeholder {
  color: var(--ds-text-tertiary, rgba(0, 0, 0, 0.38));
}

.agent-input-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

/* Voice-to-text (RM-163) */
.agent-mic-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 12px;
  border: none;
  background: rgba(15, 160, 145, 0.12);
  color: var(--ds-brand-primary-darker, #0FA091);
  cursor: pointer;
  transition: opacity 0.2s ease, background 0.2s ease;
}

.agent-mic-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.agent-mic-btn:not(:disabled):hover {
  opacity: 0.85;
}

.agent-mic-cancel {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 12px;
  border: none;
  background: transparent;
  color: rgba(58, 53, 65, 0.6);
  cursor: pointer;
}

.agent-mic-timer {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: rgba(58, 53, 65, 0.6);
  flex: none;
}

/* Live waveform (RM-163 UI v2) — bar heights track real mic levels. */
.agent-voice-wave {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 3px;
  height: 26px;
  min-width: 0;
  overflow: hidden;
}

.agent-voice-bar {
  flex: none;
  width: 3px;
  min-height: 4px;
  border-radius: 2px;
  background: var(--ds-brand-primary-darker, #0FA091);
  transition: height 90ms linear;
}

.agent-voice-error {
  margin: 6px 4px 0;
  font-size: 13px;
  color: rgb(var(--v-theme-error));
}

/* Pending image attachment chip (profile photo flow) — sits above the composer. */
.agent-attach-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  margin: 0 4px 8px;
}

/*
  Big enough to SEE before sending (Kapil, 2026-09-22: "the image looks so
  small and tiny until I send"). A 56px square cropped a business card down to
  a corner, so the member could not tell what they had attached. The height is
  fixed and the width follows the image, which keeps a card a card.
*/
.agent-attach-thumb {
  height: 160px;
  width: auto;
  /*
    Capped against the VIEWPORT, never a percentage. The chip shrink-wraps the
    image, so a % max-width resolved against the chip's own width: the chip
    sized to the image, then the image was squeezed to 70% of it — which left
    the remove button floating off to the right and the card letterboxed in
    grey bars (Kapil's screenshot, 2026-09-23).
  */
  max-width: min(300px, calc(100vw - 64px));
  object-fit: contain;
  border-radius: 12px;
  display: block;
  background: var(--ds-surface-muted, rgba(0, 0, 0, 0.04));
}

.agent-attach-thumb--zoomable {
  cursor: zoom-in;
}

.agent-attach-zoom img {
  display: block;
  width: 100%;
  height: auto;
}

.agent-attach-zoom__actions {
  display: flex;
  justify-content: flex-end;
  padding: 4px 8px 8px;
}

/* A PDF has no preview image — show a document tile instead */
.agent-attach-thumb--pdf {
  width: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--ds-surface-muted, rgba(0, 0, 0, 0.06));
  color: var(--ds-text-body);
}

.agent-attach-spinner {
  position: absolute;
  inset: 0;
  margin: auto;
}

.agent-attach-remove {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #1a1a1a;
  color: #fff;
  border: none;
  cursor: pointer;
  padding: 0;
}

.agent-voice-preprompt {
  border-radius: 16px;
}

.agent-voice-preprompt-text {
  padding: 20px 20px 8px;
  font-size: 15px;
  line-height: 1.5;
}

.agent-voice-preprompt-actions {
  padding: 0 12px 12px;
}

.agent-send-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 12px;
  border: none;
  background: var(--ds-brand-primary-darker, #0FA091);
  color: white;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.agent-send-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.agent-send-btn:not(:disabled):hover {
  opacity: 0.85;
}

/* History drawer overlay */
.agent-history-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 10;
  display: flex;
}

.agent-history-drawer {
  width: 80%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
}

.agent-history-drawer__list {
  flex: 1;
  overflow-y: auto;
}

/* History drawer transition */
.history-drawer-enter-active,
.history-drawer-leave-active {
  transition: opacity 0.25s ease;
}

.history-drawer-enter-active .agent-history-drawer,
.history-drawer-leave-active .agent-history-drawer {
  transition: transform 0.25s ease;
}

.history-drawer-enter-from {
  opacity: 0;
}

.history-drawer-enter-from .agent-history-drawer {
  transform: translateX(-100%);
}

.history-drawer-leave-to {
  opacity: 0;
}

.history-drawer-leave-to .agent-history-drawer {
  transform: translateX(-100%);
}

/* History search field */
.agent-history-search.v-input {
  margin-bottom: 0 !important;
}

.agent-history-search :deep(.v-field) {
  border-radius: 999px;
  box-shadow: none;
  border: 1px solid var(--ds-border-light);
}

.agent-history-search :deep(.v-field--focused) {
  box-shadow: none;
  background-color: var(--ds-surface-inset);
  border-color: #d1d5db;
}

.agent-history-search :deep(.v-field__prepend-inner) {
  padding-left: 12px;
  color: #6b7280;
}

/* iMessage-style timestamp separator */
.agent-message-timestamp {
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: var(--ds-text-tertiary, rgba(0, 0, 0, 0.45));
  padding: 8px 0 6px;
}

.agent-message-timestamp:first-child {
  padding-top: 0;
}

/* Typing dots animation */
/* "Checking general availability..." row: same 720px reading column as the messages and the typing
   dots (the drag layer is full width and does not cap its children). */
.agent-messages-drag > .agent-tool-status {
  width: 100%;
  max-width: 720px;
  align-self: center;
}

.agent-typing {
  width: 100%;
  max-width: 720px;
  align-self: center;
  padding-left: 64px !important;
  padding-bottom: 0 !important;
  margin-bottom: -16px;
}

.agent-typing__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--ds-brand-primary-dark);
  animation: agent-typing-bounce 1.4s ease-in-out infinite;
}

.agent-typing__dot:nth-child(2) {
  animation-delay: 0.2s;
}

.agent-typing__dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes agent-typing-bounce {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.3;
  }
  30% {
    transform: translateY(-4px);
    opacity: 1;
  }
}
</style>
